import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";
import { useBookingCodeStore } from "../stores/bookingCodeStore";
import { useBookingCodeCommit } from "./useBookingCodeCommit";
import { BookingCodeRulesList } from "./BookingCodeRulesTooltip";
import { BookingCodeFormatHint } from "./BookingCodeFormatHint";

interface MobileBookingCodeBlockProps {
  siteId: string;
  /** Draft value owned by the sheet (committed on the primary submit in
   *  search mode). The block stages edits back via `onDraftChange`. */
  draft: string;
  onDraftChange: (value: string) => void;
  /** `configuration.showBookingCodeInfo` — gates the inline rules card. */
  showRules: boolean;
}

/**
 * Shared by the Apply and remove buttons: the min width keeps the row from
 * reflowing when one replaces the other. It has to clear the longest "Apply"
 * label (sv "Tillämpa"), which `px-5` alone does not guarantee against an
 * icon-only button.
 */
const ACTION_BUTTON_CLASSES =
  "flex h-10 min-w-[6.5rem] shrink-0 items-center justify-center rounded-full bg-primary px-5 text-sm font-medium text-on-primary transition-opacity";

/**
 * Shared mobile booking-code block: label + input + "Apply", with the resolved
 * rules rendered inline as a card (no click popover, unlike the desktop pill).
 * Reused by the search sheet and the room-info ("Se tillgänglighet") sheet.
 *
 * Apply is preview-only — it resolves the code into the store and reveals the
 * card without closing the sheet or running a search. The sheet's primary
 * submit still commits the draft as before (search mode), and in room-info
 * mode Apply is the way to activate the code (pricing reads it from the store).
 */
export const MobileBookingCodeBlock: React.FC<MobileBookingCodeBlockProps> = ({
  siteId,
  draft,
  onDraftChange,
  showRules,
}) => {
  const { t } = useTranslation();
  const commitBookingCode = useBookingCodeCommit(siteId);
  const applied = useBookingCodeStore((s) => s.applied);
  const appliedCode = useBookingCodeStore((s) => s.code);
  const rules = useBookingCodeStore((s) => s.rules);
  const [isApplying, setIsApplying] = useState(false);

  const trimmed = draft.trim();
  const isAppliedDraft = applied && trimmed === (appliedCode ?? "");
  const canApply = trimmed.length > 0 && !isAppliedDraft && !isApplying;

  const handleApply = async () => {
    if (!canApply) return;
    setIsApplying(true);
    try {
      await commitBookingCode(draft);
    } catch {
      // Fired as `void handleApply()`, so an uncaught rejection here was just an
      // unhandled promise. The mutation's error handler does the reporting.
    } finally {
      setIsApplying(false);
    }
  };

  // Immediate removal — clears both the draft and the store so prices revert
  // without needing the sheet's primary submit.
  const handleClear = async () => {
    onDraftChange("");
    // Same reason as the desktop segment: clear the reservation, not just the
    // store, so the price follows the pill.
    try {
      await commitBookingCode("");
    } catch {
      // Wired straight to onClick — see the desktop segment's handleClear.
    }
  };

  return (
    <div className="border-highlighted-backplate rounded-2xl border bg-surface-card">
      <div className="p-4">
        <div className="mb-2 flex items-center gap-1.5">
          <span className="text-base font-bold">
            {t("booking_code.input_label")}
          </span>
          <BookingCodeFormatHint />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            disabled={isAppliedDraft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void handleApply();
              }
            }}
            aria-label={t("booking_code.input_label")}
            data-testid="booking-code-input"
            className="h-10 w-full flex-1 rounded-full border border-border bg-surface-card px-3 text-sm text-ink focus:outline-none disabled:bg-surface-hover disabled:text-ink/50"
          />
          {/* Once the draft matches the applied code, Apply has nothing left to
              do — the slot becomes the removal control instead of a dead
              disabled button. Removing re-enables the field. */}
          {isAppliedDraft ? (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t("booking_code.clear")}
              data-testid="booking-code-clear"
              className={ACTION_BUTTON_CLASSES}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleApply()}
              disabled={!canApply}
              data-testid="booking-code-apply"
              className={`${ACTION_BUTTON_CLASSES} disabled:opacity-40`}
            >
              {t("booking_code.apply")}
            </button>
          )}
        </div>
        {/* Only while the input still matches the applied code — editing the
            field hides the (now-stale) card until the new code is applied.
            rules.length guards the bordered card: codes without display rules
            (including ones that didn't resolve) must not render an empty box. */}
        {showRules && isAppliedDraft && rules.length > 0 && (
          <div className="mt-3 rounded-2xl border border-border bg-surface-card-secondary p-3">
            <BookingCodeRulesList rules={rules} />
          </div>
        )}
      </div>
    </div>
  );
};
